import { OrganizationUnitItem, ReferenceItem } from '@features/user/models/filter-data.model';

export type PersonEventCategory = 'Collaboration' | 'MinisterialActivity' | 'ReligiousEvent';
export type PersonEventFilter = 'All' | PersonEventCategory;
export type MinistryStatus = 'Active' | 'Licensed' | 'Retired' | 'Terminated';
export type MinistryEventType =
  | 'Ordination'
  | 'Reinstatement'
  | 'LeaveOfAbsence'
  | 'Transfer'
  | 'Death'
  | 'Resignation'
  | 'Dismissal'
  | 'Retirement'
  | 'Confirmation'
  | 'Reassignment'
  | 'AppointmentAssignment'
  | 'AppointmentEnd'
  | 'ApostleAreaTransferIn'
  | 'ApostleAreaTransferOut'
  | 'MinisterialActivityExtension'
  | 'MinisterialLicenseRenewal'
  | 'FormerMinisterAsOfficiant'
  | 'NewOfficiant'
  | 'MinisterialInstitution';
export type MinisterialActivityKind = 'Ministry' | 'Appointment' | 'Assignment';
export type MinisterialAreaChangeType = 'Added' | 'Ended';

export type ReligiousEventType =
  | 'Baptism'
  | 'Sealing'
  | 'Confirmation'
  | 'Adoption'
  | 'Wedding'
  | 'WeddingAnniversary'
  | 'Engagement'
  | 'PrenatalBlessing';

export interface PersonEvent {
  id: number;
  category: PersonEventCategory;
  categoryName: string;
  eventKind: string;
  eventTypeKey: string;
  eventTypeName: string;
  eventDate: string;
  endDate?: string;
  organizationUnitId?: string;
  organizationUnitName?: string;
  actOrganizationUnitId?: string;
  actOrganizationUnitName?: string;
  detail?: string;
  notes?: string;
  canEdit: boolean;
  canDelete: boolean;
  ministryId?: number;
  ministryStatus?: MinistryStatus;
  appointmentId?: number;
  assignmentId?: number;
  requiresActiveMinistry?: boolean;
  activityId?: number;
  activityRoleId?: number;
  religiousEventType?: ReligiousEventType;
  locationOrganizationUnitId?: string;
  locationOrganizationUnitName?: string;
  location?: string;
  anniversaryYears?: number;
  relatedPersonId?: string;
  relatedPersonName?: string;
  relatedPersonCanView?: boolean;
  ministerialAreas: MinisterialActivityArea[];
  generatedActivityChanges?: MinisterialActivityArea[];
  generatedMembershipTransfer?: {
    fromOrganizationUnitId?: string;
    fromOrganizationUnitName?: string;
    toOrganizationUnitId: string;
    toOrganizationUnitName: string;
  };
}

export interface MinisterialActivityArea {
  ministryChangeId: number;
  recordId: number;
  kind: MinisterialActivityKind;
  referenceId: number;
  referenceName: string;
  organizationUnitId?: string;
  organizationUnitName: string;
  isHistoricalOrganizationUnit: boolean;
  changeType: MinisterialAreaChangeType;
  validFrom: string;
  validTo?: string;
}

export interface MinisterialActivityEffectiveDesignation {
  kind: MinisterialActivityKind;
  referenceId: number;
  referenceName: string;
  organizationUnitId?: string;
  organizationUnitName: string;
  isHistoricalOrganizationUnit: boolean;
}

export interface MinisterialActivityEffectiveState {
  effectiveDate: string;
  ministryId?: number;
  ministryName?: string;
  status?: MinistryStatus;
  designations: MinisterialActivityEffectiveDesignation[];
}

export interface MinisterialActivityTransitionPreview {
  before: MinisterialActivityEffectiveState;
  after: MinisterialActivityEffectiveState;
  added: MinisterialActivityEffectiveDesignation[];
  ended: MinisterialActivityEffectiveDesignation[];
  unchanged: MinisterialActivityEffectiveDesignation[];
  warnings: string[];
}

export interface MinisterialActivityActionDefinition {
  eventType: MinistryEventType;
  requiresEffectiveDate: boolean;
  requiresActPlace: boolean;
  requiresComment: boolean;
  requiresMinistry: boolean;
  requiresAppointmentOrAssignment: boolean;
  requiresArea: boolean;
  requiresExpectedEndDate: boolean;
  targetStatus?: MinistryStatus;
}

export interface PersonLookupOption {
  id: string;
  name: string;
  subtitle?: string;
}

export interface PersonEventActivityOption {
  id: number;
  name: string;
}

export interface PersonEventActivityRoleOption {
  id: number;
  activityId: number;
  name: string;
}

export interface PersonEventEditorContext {
  units: OrganizationUnitItem[];
  communities: OrganizationUnitItem[];
  ministries: ReferenceItem[];
  appointments: ReferenceItem[];
  assignments: ReferenceItem[];
  activities: PersonEventActivityOption[];
  activityRoles: PersonEventActivityRoleOption[];
  currentMinisterialAreas: MinisterialActivityArea[];
}

export interface CreatePersonEventRequest {
  category: PersonEventCategory;
  eventDate: string;
  endDate?: string;
  organizationUnitId?: string;
  activityId?: number;
  activityRoleId?: number;
  religiousEventType?: ReligiousEventType;
  locationOrganizationUnitId?: string;
  location?: string;
  anniversaryYears?: number;
  relatedPersonId?: string;
  relatedPersonName?: string;
  notes?: string;
}

export type UpdatePersonEventRequest = CreatePersonEventRequest;

export const personEventCategoryOptions: Array<{ value: PersonEventCategory; label: string }> = [
  { value: 'Collaboration', label: 'Colaboración en la Iglesia' },
  { value: 'MinisterialActivity', label: 'Actividad ministerial' },
  { value: 'ReligiousEvent', label: 'Sacramentos y bendiciones' },
];

export const personEventFilterOptions: Array<{ value: PersonEventFilter; label: string }> = [
  { value: 'All', label: 'Todos' },
  ...personEventCategoryOptions,
];

export type ReligiousEventCategory = 'Sacrament' | 'Blessing';

export interface ReligiousEventTypeOption {
  value: ReligiousEventType;
  label: string;
  category: ReligiousEventCategory;
  isUnique: boolean;
}

export const religiousEventTypeOptions: ReligiousEventTypeOption[] = [
  { value: 'Baptism',           label: 'Bautismo',                  category: 'Sacrament', isUnique: true  },
  { value: 'Sealing',           label: 'Sellamiento',               category: 'Sacrament', isUnique: true  },
  { value: 'Adoption',          label: 'Adopción',                  category: 'Sacrament', isUnique: true  },
  { value: 'Confirmation',      label: 'Confirmación',              category: 'Blessing',  isUnique: true  },
  { value: 'Wedding',           label: 'Bendición matrimonial',     category: 'Blessing',  isUnique: false },
  { value: 'WeddingAnniversary',label: 'Aniversario de Bodas', category: 'Blessing',  isUnique: false },
  { value: 'Engagement',        label: 'Compromiso',                category: 'Blessing',  isUnique: false },
  { value: 'PrenatalBlessing',  label: 'Bendición prenatal',        category: 'Blessing',  isUnique: false },
];
